import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import EPageHeader from '@/components/everridge/EPageHeader';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import { useUserRole } from '@/features/identity/hooks/useUserRole';
import { Roles } from '@/@types/auth.types';
import { useTenantsListApi } from '@/features/superAdmin/features/tenants/hooks/useTenantsListApi';
import ETableWithApiPagination from '@/components/everridge/table/ETableWithApiPagination';
import { useTenantsFilter } from '@/features/superAdmin/features/tenants/hooks/useTenantsFilter';
import TenantsPageColumns from '@/features/superAdmin/features/tenants/components/TenantsPageColumns';
import ESearchInput from '@/components/everridge/ESearchInput';
import TenantManage from './components/TenantManage';
import DatabaseBackupDialog from './components/DatabaseBackupDialog';
import AddIcon from '@/components/icons/AddIcon';
import { Button } from '@/components/ui/button';
import { DatabaseBackup } from 'lucide-react';

const SuperAdminTenantsPage = () => {
	const [tenantId, setTenantId] = useState('');
	const [showTenantDetails, setShowTenantDetails] = useState(false);
	const [showBackupDialog, setShowBackupDialog] = useState(false);
	const [page, setPage] = useState(0);
	const [searchText, setSearchText] = useState('');

	const { t } = useTranslation();
	const columns = TenantsPageColumns();
	const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);
	const userRole = useUserRole();

	const { tenantsListData } = useTenantsListApi({ searchText });

	const { filter, setFilter } = useTenantsFilter();

	if (userRole !== Roles.superAdmin) return null;

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
		setFilter({
			skip: filter.take * newPage,
		});
	};

	const handleSearchText = (text: string) => {
		setPage(0);
		setSearchText(text);
	};

	const handleShowDetails = (id: string) => {
		setTenantId(id);
		setShowTenantDetails(true);
	};

	const handleCreateTenant = () => {
		setTenantId('');
		setShowTenantDetails(true);
	};

	return (
		<section
			className={cn('flex-1 flex flex-col gap-6', isMobile && 'gap-4')}>
			<EPageHeader
				title={t('main.superAdmin.tenants.title')}
				search={
					<ESearchInput
						value={searchText}
						setValue={handleSearchText}
						placeholder={t(
							'main.superAdmin.tenants.form.search.placeholder',
						)}
						containerClassName={cn(!isMobile && 'w-60')}
					/>
				}>
				<Button
					variant='outline'
					size={isMobile ? 'icon' : 'default'}
					onClick={() => setShowBackupDialog(true)}>
					<DatabaseBackup className='size-4' />
					{!isMobile &&
						t('main.superAdmin.tenants.components.databaseBackup.button')}
				</Button>
				<Button
					variant='outline'
					size={isMobile ? 'icon' : 'default'}
					onClick={handleCreateTenant}>
					<AddIcon />
					{!isMobile && t('elements.buttons.addTenant')}
				</Button>
			</EPageHeader>
			<div
				className={cn(
					'h-full flex flex-col gap-6',
					isMobile && 'gap-4',
				)}>
				<ETableWithApiPagination
					columns={columns}
					data={tenantsListData}
					paginationStep={filter.take}
					page={page}
					setPage={handlePageChange}
					showDetails={handleShowDetails}
				/>
			</div>
			{showTenantDetails && (
				<TenantManage
					id={tenantId}
					onClose={setShowTenantDetails}
				/>
			)}
			<DatabaseBackupDialog
				open={showBackupDialog}
				onOpenChange={setShowBackupDialog}
			/>
		</section>
	);
};

export default SuperAdminTenantsPage;
